/* eslint-disable react/jsx-wrap-multilines */
import React, { PropsWithChildren, ReactNode } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '@helpers/className';
import Heading from '@solar/common/Heading/Heading';
import Link from 'next/link';
import rpLogo from '@assets/images/rp-logo-mark.svg';
import Image from 'next/legacy/image';
import Paragraph from '@solar/common/Paragraph/Paragraph';
import { getSlot } from '@components/common/Slot';
import { CloseIcon } from '@resortpass/solar-icons';
import { CollapsibleContainer2 } from '@components/common/CollapsibleContainer/CollapsibleContainer';

export interface TitleProps {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const MenuTitle = React.forwardRef<HTMLHeadingElement, TitleProps>(
  ({ asChild = false, children, className, ...props }, ref) => {
    const cls = cn('hidden text-solar-primary desktop:block', className);
    const Comp = asChild ? Slot : Heading;

    return (
      <Comp ref={ref} className={cls} {...props} level={3}>
        {children}
      </Comp>
    );
  },
);

MenuTitle.displayName = 'Menu.Title';

export interface MenuDefaultOptionsProps {
  children: ReactNode;
  className?: string;
}

export const MenuDefaultOptions = React.forwardRef<HTMLDivElement, MenuDefaultOptionsProps>(
  ({ children, className }, ref) => {
    const cls = cn('flex flex-col gap-spacing-sm desktop:gap-spacing-lg', className);

    return (
      <div ref={ref} className={cls}>
        {children}
      </div>
    );
  },
);

MenuDefaultOptions.displayName = 'Menu.DefaultOptions';

export interface MenuLinkProps {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
  href?: string;
  size?: 'sm' | 'md' | 'lg';
  title?: string;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

export interface MenuAppDownloadProps {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const MenuAppDownload = React.forwardRef<HTMLDivElement, MenuAppDownloadProps>(
  ({ asChild = false, children, className, ...props }, ref) => {
    const cls = cn('flex flex-col gap-spacing-sm desktop:hidden', className);
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp ref={ref} className={cls} {...props}>
        {children}
      </Comp>
    );
  },
);

MenuAppDownload.displayName = 'Menu.AppDownload';

export interface MenuOptionsProps {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
}

export const MenuOptions = React.forwardRef<HTMLDivElement, MenuOptionsProps>(
  ({ asChild = false, children, className, ...props }, ref) => {
    const cls = cn(
      'flex flex-col overflow-y-auto pb-spacing-xl desktop:gap-spacing-lg desktop:flex-row desktop:overflow-y-hidden desktop:pb-0',
      className,
    );
    const Comp = asChild ? Slot : 'div';
    return (
      <Comp ref={ref} className={cls} {...props}>
        {children}
      </Comp>
    );
  },
);

MenuOptions.displayName = 'Menu.Options';

export interface MenuLabelProps {
  asChild?: boolean;
  children: ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function MenuLabel({
  asChild = false,
  children,
  className,
  size = 'sm',
  ...props
}: MenuLabelProps) {
  const cls = cn('text-solar-primary desktop:text-solar-secondary', className);
  const Comp = asChild ? Slot : Paragraph;
  return (
    <Comp className={cls} {...props} size={size}>
      {children}
    </Comp>
  );
}

MenuLabel.displayName = 'Menu.Label';

export interface MenuCollapsibleGroupProps {
  children: ReactNode;
  title: string;
  className?: string;
  initiallyExpanded?: boolean;
}

export const MenuCollapsibleGroup = React.forwardRef<HTMLDivElement, MenuCollapsibleGroupProps>(
  ({ children, title, className, initiallyExpanded = false }, ref) => {
    const cls = cn('flex flex-col', className);

    return (
      <div ref={ref} className={cls}>
        {/* Desktop version - regular group */}
        <div
          className="hidden desktop:flex flex-col space-y-spacing-lg"
          style={{ minWidth: '198px' }}
        >
          <MenuLabel>{title}</MenuLabel>

          {children}
        </div>

        {/* Mobile version - collapsible */}
        <div className="flex flex-col desktop:hidden">
          <CollapsibleContainer2
            initiallyExpanded={initiallyExpanded}
            title={
              <MenuLabel size="md" className="py-spacing-md">
                {title}
              </MenuLabel>
            }
          >
            <div className="flex flex-col ml-spacing-md pb-spacing-md space-y-spacing-sm">
              {children}
            </div>
          </CollapsibleContainer2>
        </div>
      </div>
    );
  },
);

MenuCollapsibleGroup.displayName = 'Menu.CollapsibleGroup';

export function Menu({
  children,
  onClose,
  isVisible = true,
}: PropsWithChildren<{ onClose: () => void; isVisible?: boolean }>) {
  const items = React.Children.toArray(children);

  const appDownload = getSlot(items, MenuAppDownload);
  const defaultOptions = getSlot(items, MenuDefaultOptions);
  const menuOptions = getSlot(items, MenuOptions);
  const title = getSlot(items, MenuTitle);

  const handleCloseMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onClose();
    }
  };

  return (
    <div className={cn(!isVisible && 'invisible opacity-0 pointer-events-none')}>
      <div
        className={cn(
          'fixed top-0 right-0 text-solar-icon-inverted z-600 desktop:hidden transition-opacity duration-300',
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      >
        <button
          onClick={onClose}
          type="button"
          aria-label="close menu"
          className="flex items-center justify-center p-spacing-md"
        >
          <CloseIcon size={32} />
        </button>
      </div>
      <nav
        className={cn(
          'fixed top-0 bg-solar-surface-primary transition-opacity duration-300 ease-in-out h-dvh w-82% px-spacing-md py-spacing-lg z-600 desktop:z-300 desktop:py-spacing-xl desktop:px-spacing-2xl desktop:h-auto desktop:top-127px desktop:w-full',
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      >
        <div className="block desktop:hidden">
          <Link href="/">
            <div className="h-4 relative w-188px">
              <Image src={rpLogo} alt="ResortPass" layout="fill" />
            </div>
          </Link>
        </div>

        <div className="flex flex-col-reverse justify-between pt-spacing-lg h-full desktop:flex-row desktop:mx-auto desktop:max-w-1312px desktop:pt-0">
          <div className="flex flex-col gap-spacing-lg max-w-421px">
            {title}

            {defaultOptions}

            <div className="flex flex-col gap-spacing-sm desktop:hidden">{appDownload}</div>
          </div>

          {menuOptions}
        </div>
      </nav>
      <div
        className={cn(
          'opacity-75 fixed inset-y-0 left-0 w-full h-screen bg-black transform transition-all duration-300 ease-in-out z-500 desktop:z-200',
          isVisible ? 'opacity-75' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
        onKeyDown={handleCloseMenuKeyDown}
        role="button"
        tabIndex={0}
        aria-label="close menu"
      />
    </div>
  );
}

Menu.AppDownload = MenuAppDownload;
Menu.CollapsibleGroup = MenuCollapsibleGroup;
Menu.DefaultOptions = MenuDefaultOptions;
Menu.Options = MenuOptions;
Menu.Label = MenuLabel;
Menu.Title = MenuTitle;
